import { useQuery } from "@tanstack/react-query";
import { Card, Flex, Heading, Text, Badge } from "@radix-ui/themes";
import { getSignupStats, getUsage } from "../api/admin";
import { usd, num, tokens } from "../lib/format";

function Kpi({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <Card size="2">
      <Flex direction="column" gap="1">
        <Text size="1" className="hq-muted">
          {label}
        </Text>
        <Heading size="6">{value}</Heading>
        {sub && (
          <Text size="1" className="hq-muted">
            {sub}
          </Text>
        )}
      </Flex>
    </Card>
  );
}

function Sparkline({ data }: { data: Array<{ day: string; count: number }> }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="hq-spark" aria-hidden>
      {data.map((d) => (
        <div
          key={d.day}
          className="hq-spark__bar"
          title={`${d.day}: ${d.count}`}
          style={{ height: `${(d.count / max) * 100}%` }}
        />
      ))}
    </div>
  );
}

export default function Dashboard() {
  const signups = useQuery({
    queryKey: ["signups", 30],
    queryFn: () => getSignupStats(30),
  });
  const usage = useQuery({
    queryKey: ["usage", 30],
    queryFn: () => getUsage(30),
  });

  const newLast30 = signups.data
    ? signups.data.daily.reduce((a, d) => a + d.count, 0)
    : 0;

  return (
    <Flex direction="column" gap="4">
      <Heading size="5">Overview</Heading>

      <div className="hq-cards">
        <Kpi
          label="Total users"
          value={signups.data ? num(signups.data.total) : "…"}
          sub={
            signups.data
              ? `${num(signups.data.verified)} verified`
              : undefined
          }
        />
        <Kpi
          label="New (30d)"
          value={signups.isLoading ? "…" : num(newLast30)}
        />
        <Kpi
          label="Spend (30d)"
          value={
            usage.data?.configured ? usd(usage.data.totals.costMcents) : "—"
          }
          sub={usage.data && !usage.data.configured ? "analytics off" : undefined}
        />
        <Kpi
          label="Chat turns (30d)"
          value={
            usage.data?.configured ? num(usage.data.totals.turns) : "—"
          }
        />
        <Kpi
          label="Tokens (30d)"
          value={
            usage.data?.configured
              ? tokens(
                  usage.data.totals.inputTokens +
                    usage.data.totals.outputTokens,
                )
              : "—"
          }
        />
      </div>

      <Card size="2">
        <Flex direction="column" gap="2">
          <Flex justify="between" align="center">
            <Text weight="medium">Signups · last 30 days</Text>
            {signups.data && <Badge color="gray">{num(newLast30)}</Badge>}
          </Flex>
          {signups.data ? (
            <Sparkline data={signups.data.daily} />
          ) : (
            <Text size="1" className="hq-muted">
              Loading…
            </Text>
          )}
        </Flex>
      </Card>

      {(signups.error || usage.error) && (
        <Text size="2" color="red">
          {String((signups.error ?? usage.error) as Error)}
        </Text>
      )}
    </Flex>
  );
}
